import React, { useEffect, useLayoutEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import cs from "classnames";

import { COLORS, SIZES } from "./utilities";
import classes from "./SearchBar.module.scss";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { observer } from "mobx-react-lite";
import { isMobile } from "react-device-detect";
import Icon, { NAMES } from "../Icon";

const SearchBarComponent = ({
  children: icon,
  error,
  page,
  id,
  type,
  isSearching,
  onSearch,
  searchingOptions,
  onChange,
  value,
  ...properties
}, forwardedRef) => {
  const ref = React.createRef();
  const searchBarRef = useRef(null);
  const [isOutOfViewport, setIsOutOfViewport] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // Expose methods to parent component
  useImperativeHandle(forwardedRef, () => ({
    getInputValue: () => {
      const input = ref.current?.getInput();
      return input ? input.value : '';
    },
    focusInput: () => {
      ref.current?.focus();
    },
    clearInput: () => {
      ref.current?.clear();
    }
  }));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onChange(e.target.value);
      ref.current.hideMenu();
    }
  };

  useEffect(() => {
    console.log("Search Value", value);
    let timer = setTimeout(() => {
      const input = ref.current?.getInput();
      if (input) {
        input.value = value;
      }
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [value]);

  const addClasses = () => {
    if (page == "library") {
      return classes.library;
    }
    if (page == "kids") {
      return classes.kidsLayout;
    }
    if (page == "illuminate") {
      return classes.illuminate;
    }
    if (page == "daily-rebbe-video") {
      return classes.dailyRebbeVideo;
    }
    return classes[page];
  };

  const [isFloatingVisible, setIsFloatingVisible] = useState(false);

  const getOffsetTop = (elem) => {
    let distance = 0;
    if (elem.offsetParent) {
      do {
        distance += elem.offsetTop;
        elem = elem.offsetParent;
      } while (elem);
    }
    return distance < 0 ? 0 : distance;
  };

  const handleScroll = () => {
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    const mainSearchBarOffsetTop = getOffsetTop(searchBarRef.current);

    if (scrollTop > mainSearchBarOffsetTop) {
      setIsFloatingVisible(true);
    } else {
      setIsFloatingVisible(false);
    }
  };

  useEffect(() => {
    if (isMobile) {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  useLayoutEffect(() => {
    const searchBarMain = document.querySelector(".searchBarMain");

    const observeSearchBarTop = () => {
      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver((entries) => {
          const [entry] = entries;
          setIsOutOfViewport(!entry.isIntersecting);
        });

        if (searchBarMain) {
          observer.observe(searchBarMain);
        }

        return () => {
          console.log("search value: inside return");
        };
      }
    };
    observeSearchBarTop();
  }, [searchBarRef]);

  useEffect(() => {
    const handleClickOutside = () => {
      setIsClicked(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBarRef]);

  const focusTypeahead = () => {
    ref.current?.focus();
  };

  const shouldShowFloatingBar = isMobile
    ? isFloatingVisible && isOutOfViewport
    : isOutOfViewport;

  return (
    <>
      <div className={cs(classes.container, addClasses())} ref={searchBarRef}>
        <div className="searchBarMain">
          <AsyncTypeahead
            emptyLabel=""
            ref={ref}
            isLoading={isSearching}
            onSearch={onSearch}
            options={searchingOptions}
            id={id}
            type={type}
            onChange={(selectedOptions) => {
              onChange(selectedOptions.pop());
            }}
            onInputChange={(text) => {
              try {
                let items = document.querySelector(
                  "#async-autocomplete-search"
                );
                let firstChild = items.firstChild;
                if (firstChild.classList.contains("disabled")) {
                  items.style.visibility = "hidden";
                } else {
                  items.style.visibility = "";
                }
              } catch (e) {
                console.warn("Soft Warn:", e);
              }
              if (text == "") {
                ref.current.clear();
              }
            }}
            defaultInputValue={value}
            onKeyDown={handleKeyDown}
            className={cs(classes.asyncWrapper, {
              [classes.error]: error,
              [classes.withIcon]: icon,
            })}
            {...properties}
          />
          {icon && (
            <div
              onClick={focusTypeahead}
              className={cs(classes.icon, { [classes.error]: error })}
            >
              {icon}
            </div>
          )}
          <button
            style={{ display: "none" }}
            id="hiddenAsyncInputClear"
            onClick={() => {
              ref.current.clear();
            }}
          >
            Clear
          </button>
        </div>
      </div>
      {shouldShowFloatingBar && (
        <div
          className={cs(
            classes.floatContainer,
            classes.fixedSearchBar,
            !isMobile && isClicked && classes.widthFull,
            isMobile && !isClicked && classes.mobileFixedSearchBar,
            isMobile && isClicked && classes.mobileWidthFull,
            addClasses()
          )}
          onClick={() => setIsClicked(true)}
        >
          <AsyncTypeahead
            emptyLabel=""
            ref={ref}
            isLoading={isSearching}
            onSearch={onSearch}
            options={searchingOptions}
            id={id}
            type={type}
            onChange={(selectedOptions) => {
              onChange(selectedOptions.pop());
            }}
            onInputChange={(text) => {
              try {
                let items = document.querySelector(
                  "#async-autocomplete-search"
                );
                let firstChild = items.firstChild;
                if (firstChild.classList.contains("disabled")) {
                  items.style.visibility = "hidden";
                } else {
                  items.style.visibility = "";
                }
              } catch (e) {
                console.warn("Soft Warn:", e);
              }
              if (text == "") {
                ref.current.clear();
              }
            }}
            defaultInputValue={value}
            onKeyDown={handleKeyDown}
            className={cs(classes.asyncWrapper, addClasses(), {
              [classes.error]: error,
              [classes.withIcon]: icon,
            })}
            {...properties}
          />
          {icon && (
            <div
              onClick={focusTypeahead}
              className={cs(classes.icon, { [classes.error]: error })}
            >
              {icon}
            </div>
          )}
          {isMobile && isClicked && (
            <div className={cs(classes.crossIcon)}>
              <Icon
                name={NAMES.CrossButton}
                size="xs"
                onClick={() => setIsClicked(false)}
              />
            </div>
          )}
          <button
            style={{ display: "none" }}
            id="hiddenAsyncInputClear"
            onClick={() => {
              ref.current.clear();
            }}
          >
            Clear
          </button>
        </div>
      )}
    </>
  );
};

SearchBarComponent.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(Object.values(COLORS)),
  error: PropTypes.string,
  id: PropTypes.string,
  page: PropTypes.string,
  size: PropTypes.oneOf(Object.values(SIZES)),
  stretched: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.oneOf(["number", "password", "text"]),
  value: PropTypes.string,
  isSearching: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  searchingOptions: PropTypes.array,
};

SearchBarComponent.defaultProps = {
  children: null,
  color: COLORS.PRIMARY,
  error: "",
  page: "",
  size: SIZES.MEDIUM,
  stretched: false,
  label: "",
  type: "text",
  isSearching: false,
  searchingOptions: [],
};

const SearchBar = observer(forwardRef(SearchBarComponent));
SearchBar.displayName = "SearchBar";

export default SearchBar;